import { useState } from 'react';
import {
  Alert as MuiAlert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import PageHeader from '@/content/Management/PosTransactions/PageHeader';
import PosTransactionsTable from '@/content/Management/PosTransactions/PosTransactionsTable';
import OffDevicePosRefundDialog from '@/components/POS/OffDevicePosRefundDialog';
import { OffDeviceRefundResult, posService } from '@/data/posService';
import {
  PosTransactionListParams,
  PosTransactionWithBranch
} from '@/types/PosTransaction.interface';
import { Filters } from '@/types/Filters';
import {
  isOffDeviceRefundTransaction,
  needsManualReview,
  readRefundOutcome
} from '@/utils/posRefund';

/**
 * SUPER_ADMIN-only cross-tenant POS transaction inspector — Hasan Bey /
 * Pavo support ekranı. Route is hidden from non-superadmins via the sidebar
 * gate; the backend also rejects cross-branch filters (companyId /
 * providerId / date range / search — plus omitting branchId altogether) for
 * every other role, so a direct URL visit would only load an empty list at
 * worst, never leak cross-tenant data.
 */
const PosTransactionsManagement = () => {
  const { t } = useTranslation();

  const [transactions, setTransactions] = useState<PosTransactionWithBranch[]>(
    []
  );
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [cancelTarget, setCancelTarget] =
    useState<PosTransactionWithBranch | null>(null);
  const [cancelInFlight, setCancelInFlight] = useState(false);

  const [refundTarget, setRefundTarget] =
    useState<PosTransactionWithBranch | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundInFlight, setRefundInFlight] = useState(false);
  const [offDeviceTarget, setOffDeviceTarget] =
    useState<PosTransactionWithBranch | null>(null);

  const [lastFilters, setLastFilters] = useState<PosTransactionListParams>({});

  /**
   * `filters.startDate`/`endDate` come from a native `<input type="date">` as
   * a bare `YYYY-MM-DD` — always the ADMIN'S OWN local calendar date. Passing
   * that string straight through would have the backend do `new Date(str)`,
   * which per the ECMAScript date-only string spec parses as UTC midnight,
   * not local midnight — silently shifting the boundary by the browser's UTC
   * offset. Confirmed live 2026-07-30: a transaction created at 22:26 UTC
   * (01:26 Turkey time on the 30th) was excluded from a "From: 30/07/2026"
   * filter because its UTC day was still the 29th, even though it's plainly
   * "the 30th" to anyone reading the table (which renders in local time).
   * Appending a bare time with no 'Z'/offset makes the Date constructor
   * parse it in the browser's OWN local time instead, matching what's shown.
   */
  const localDateToUtcIso = (dateStr: string): string =>
    new Date(`${dateStr}T00:00:00`).toISOString();

  const fetchTransactions = async (filters?: Filters) => {
    // Table's `page` is 0-based (MUI TablePagination); the backend DTO expects
    // 1-based. Convert here rather than leak two conventions into the service.
    const params: PosTransactionListParams = {
      status: filters?.status as PosTransactionListParams['status'],
      search: filters?.search || undefined,
      createdAtFrom: filters?.startDate
        ? localDateToUtcIso(filters.startDate)
        : undefined,
      createdAtTo: filters?.endDate
        ? // Inclusive upper bound: roll to the START of the NEXT local day
          // (backend uses `lt` on createdAt, not `lte`).
          new Date(
            new Date(localDateToUtcIso(filters.endDate)).getTime() +
              24 * 60 * 60 * 1000
          ).toISOString()
        : undefined,
      page: (filters?.page ?? 0) + 1,
      limit: filters?.limit ?? 25
    };
    setLastFilters(params);
    setLoading(true);
    try {
      const response = await posService.listTransactions(params);
      setTransactions(response.items as PosTransactionWithBranch[]);
      setTotalCount(response.total);
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message ?? t('posTransactions.error.list')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters: Filters) => {
    void fetchTransactions(filters);
  };

  const openCancel = (transaction: PosTransactionWithBranch) => {
    setCancelTarget(transaction);
  };
  const closeCancel = () => {
    if (cancelInFlight) return;
    setCancelTarget(null);
  };
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelInFlight(true);
    try {
      await posService.cancelTransaction(cancelTarget.id);
      setSuccessMessage(
        t('posTransactions.cancel.success', { id: cancelTarget.id })
      );
      setCancelTarget(null);
      await fetchTransactions({ ...lastFilters } as Filters);
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message ?? t('posTransactions.cancel.error')
      );
    } finally {
      setCancelInFlight(false);
    }
  };

  const openRefund = (transaction: PosTransactionWithBranch) => {
    // Token / InPOS cannot refund on the device: record it instead.
    if (isOffDeviceRefundTransaction(transaction)) {
      setOffDeviceTarget(transaction);
      return;
    }
    setRefundTarget(transaction);
    setRefundAmount(String(transaction.amount));
    setRefundReason('');
  };
  const closeRefund = () => {
    if (refundInFlight) return;
    setRefundTarget(null);
  };
  const submitRefund = async () => {
    if (!refundTarget) return;
    const amount = Number(refundAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErrorMessage(t('posTransactions.refund.invalidAmount'));
      return;
    }
    setRefundInFlight(true);
    try {
      const refund = await posService.refundTransaction(refundTarget.id, {
        amount,
        reason: refundReason || undefined
      });
      // A 201 is not a success: the device may have rejected the refund
      // (FAILED), or the money went back but the sale could not be undone.
      const outcome = readRefundOutcome(refund);
      if (outcome.kind === 'failed') {
        setErrorMessage(
          outcome.message ??
            t('posTransactions.refund.failed', { id: refundTarget.id })
        );
        return;
      }
      if (outcome.kind === 'pending') {
        setSuccessMessage(
          t('posTransactions.refund.pending', { id: refundTarget.id })
        );
      } else {
        const after = await posService
          .getTransaction(refundTarget.id)
          .catch(() => undefined);
        if (needsManualReview(after)) {
          setErrorMessage(
            t('posTransactions.refund.manualReview', { id: refundTarget.id })
          );
        } else {
          setSuccessMessage(
            t('posTransactions.refund.success', { id: refundTarget.id })
          );
        }
      }
      setRefundTarget(null);
      await fetchTransactions({ ...lastFilters } as Filters);
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message ?? t('posTransactions.refund.error')
      );
    } finally {
      setRefundInFlight(false);
    }
  };

  const onOffDeviceRecorded = async (result: OffDeviceRefundResult) => {
    const id = result.transaction.id;
    setOffDeviceTarget(null);
    if (result.transaction.recoveryStatus === 'COMPLETED') {
      setSuccessMessage(t('posTransactions.offDevice.success', { id }));
    } else {
      setErrorMessage(t('posTransactions.offDevice.notCompleted', { id }));
    }
    await fetchTransactions({ ...lastFilters } as Filters);
  };

  return (
    <>
      <Helmet>
        <title>{t('posTransactions.management')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader />
      </PageTitleWrapper>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PosTransactionsTable
            transactions={transactions}
            loading={loading}
            totalCount={totalCount}
            onFilterChange={handleFilterChange}
            onCancelTransaction={openCancel}
            onRefundTransaction={openRefund}
            pageKey="pos-transactions"
          />
        </Grid>
      </Grid>

      {/* Cancel confirmation */}
      <Dialog
        open={Boolean(cancelTarget)}
        onClose={closeCancel}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {t('posTransactions.cancel.title', { id: cancelTarget?.id })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('posTransactions.cancel.description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancel} disabled={cancelInFlight}>
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmCancel}
            disabled={cancelInFlight}
          >
            {t('posTransactions.cancel.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund dialog */}
      <Dialog
        open={Boolean(refundTarget)}
        onClose={closeRefund}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {t('posTransactions.refund.title', { id: refundTarget?.id })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('posTransactions.refund.amount')}
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              fullWidth
              inputProps={{
                min: 0,
                step: '0.01',
                max: refundTarget ? Number(refundTarget.amount) : undefined
              }}
              helperText={
                refundTarget
                  ? t('posTransactions.refund.max', {
                      amount: Number(refundTarget.amount).toFixed(2),
                      currency: refundTarget.currency
                    })
                  : ''
              }
            />
            <TextField
              label={t('posTransactions.refund.reason')}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefund} disabled={refundInFlight}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={submitRefund}
            disabled={refundInFlight}
          >
            {t('posTransactions.refund.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <OffDevicePosRefundDialog
        transaction={offDeviceTarget}
        onClose={() => setOffDeviceTarget(null)}
        onRecorded={onOffDeviceRecorded}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
      >
        <MuiAlert
          variant="filled"
          severity="success"
          onClose={() => setSuccessMessage(null)}
        >
          <Typography>{successMessage}</Typography>
        </MuiAlert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(errorMessage)}
        autoHideDuration={7000}
        onClose={() => setErrorMessage(null)}
      >
        <MuiAlert
          variant="filled"
          severity="error"
          onClose={() => setErrorMessage(null)}
        >
          <Typography>{errorMessage}</Typography>
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default PosTransactionsManagement;
